import { moduleHeadroomMm, shelvingCellHeadroomMm } from "./flapHeadroom";
import { shelvingCellDrawerCountById } from "./chest";
import { cellFrontLeaves, flapMotionEnglish, shelvingExcludedLifts } from "./flapFronts";
import { CARCASS_MM } from "./carcass";
import { shelvingTvPlacement } from "./shelvingTv";
import {
  baseModuleFor,
  cabinetDividerOffsets,
  cabinetSectionWidths,
  doorLeafCount,
  hasUnifiedFacade,
  hingedDoorLeafLayout,
  mezzanineFlapLayout,
  mezzanineFor
} from "./moduleRules";
import { internalItemPieceCount } from "./compartments";
import { CABINET_DRAWER_SEAM_MM, cabinetDrawerFinishDiffers, cabinetDrawerLayout } from "./cabinetDrawers";
import { cabinetLightMode, cabinetLightPosition, cabinetLightTemperature } from "./cabinetLighting";
import { hasOwnBackDecor, shelvingHasBackPanels } from "./backPanel";
import { resolveDecor } from "./decors";
import { LIGHT_TEMPERATURES, shelvingLightingCells, shelvingModuleLights } from "./lighting";
import {
  getShelvingDiagonalSections,
  getShelvingGridLayout,
  getShelvingCellBoards,
  getShelvingCellLayouts,
  getShelvingMergedCells,
  getShelvingMainDividerSegment,
  shelvingDividerEdgeCenters,
  shelvingFacadeMount,
  shelvingCellFrontRect,
  gridCellWidth
} from "./shelving";
import {
  cabinetBaseHeightMm,
  cabinetBaseSetbackMm,
  cabinetBaseType,
  leftFillerMm,
  rightFillerMm,
  topFillerMm
} from "./installation";
import {
  handleCenterYmm,
  handleEdgeOffsetMm,
  handleLengthMm,
  handleOrientation
} from "./handles";
import { endModuleDescription } from "./endModules";
import { cornerDoorWidthMm, cornerFillerMm, cornerHingeSide, cornerNeighbourFillerMm, cornerPlan, cornerRun, cornerSide } from "./corner";
import { doorFacadeOverride, leafFinish } from "./doorFacades";
import { drawerFrontFinish, hasOwnShelfDecor } from "./interiorDecor";
import {
  facadePaintHex,
  facadePaintLabel,
  facadeSupportsMilling
} from "./facades";
import {
  normalizeShelvingColorCode,
  normalizeShelvingColorHex, shelvingCarcassEnamel } from "./shelvingFinishes";
import type {
  FacadePatternType,
  FacadeType,
  HandleType,
  InternalItem,
  MaterialType,
  PlannerProject,
  WardrobeModule
} from "../types";

const materialDescription: Record<MaterialType, string> = {
  warmOak: "warm honey oak wood decor with vertical natural grain",
  linen: "light sand linen decor with a subtle textile texture",
  matteWhite: "soft matte white solid surface",
  walnut: "dark tobacco walnut with straight vertical wood grain",
  graphite: "deep matte graphite solid surface"
};

const facadeDescription: Record<FacadeType, string> = {
  wood: "SOLID OPAQUE wood or laminate panel",
  satin: "SOLID OPAQUE painted MDF/enamel panel",
  gloss: "SOLID OPAQUE routed painted MDF/enamel panel",
  glass: "transparent clear glass pane in a 50 mm wide aluminium frame",
  mirror: "full mirror panel reflecting the room"
};

const patternDescription: Record<FacadePatternType, string> = {
  plain: "flat unbroken panel",
  art001: "dense vertical routed grooves",
  art005:
    "split routed design with vertical grooves over the upper part and horizontal grooves over the lower part",
  art006: "rhythmic horizontal chevron routing",
  art007: "sculpted overlapping scale routing",
  art008: "repeating groups of three thin parallel diagonal routed grooves",
  art009: "thin minimalist routed rectangular frame",
  art010: "isometric three-dimensional cube routing",
  art012: "multi-level classic rectangular frame routing",
  art023:
    "wide beveled rectangular frame with a recessed SOLID center panel; this center is opaque material, NOT glass, NOT mirror and NOT an opening",
  art036: "dense three-dimensional honeycomb routing made from small hexagonal cells",
  art037: "dense crossed diagonal routing forming tall narrow diamonds"
};

const handleDescription: Record<HandleType, string> = {
  bar: "external bar handle",
  profile: "C-profile handle",
  recessed: "recessed handle",
  integrated: "integrated routed MDF finger-pull",
  push: "handleless push-to-open",
  glassGrip: "no separate handle: a full-height finger-pull groove built into the aluminium frame profile on the opening edge"
};

const itemDescription: Record<InternalItem["type"], string> = {
  shelf: "shelves",
  drawer: "drawers",
  rail: "hanging rails",
  basket: "wire baskets",
  leatherBasket: "pull-out baskets upholstered in brown faux leather",
  shoeRack: "flat wooden shoe shelves with a low front lip",
  trouserRack: "pull-out trouser racks with folded trousers",
  pantograph: "pantographs",
  divider: "vertical dividers",
  led: "LED lights"
};

const groupedItems = (module: WardrobeModule) => {
  const counts = new Map<InternalItem["type"], number>();
  for (const item of module.items) {
    // В широком корпусе у каждого пролёта свои полки, ящики и штанги.
    counts.set(item.type, (counts.get(item.type) ?? 0) + internalItemPieceCount(module, item));
  }
  const description = Array.from(counts, ([type, count]) =>
    `${count} ${itemDescription[type]}`
  );
  // Подсветка шкафа (замечание № 13): ленты под полками или в стойках.
  const mode = cabinetLightMode(module);
  if (mode !== "none") {
    const temperature = LIGHT_TEMPERATURES[cabinetLightTemperature(module)];
    const place = cabinetLightPosition(module) === "back" ? "near the back panel" : "near the front edge";
    description.push(mode === "shelves"
      ? `a ${temperature.kelvin} K LED strip in an aluminium profile under each lit shelf, ${place}, lighting the compartment below it`
      : `${temperature.kelvin} K LED strips in aluminium profiles running vertically along both inner side walls of every section, ${place}`);
  }
  return description.length > 0 ? description.join(", ") : "no internal fittings";
};

const moduleTypeDescription = (module: WardrobeModule) => {
  switch (module.type) {
    case "single":
      return "single-width wardrobe cabinet";
    case "double":
      return "double-width wardrobe cabinet";
    case "mezzanine":
      return "top mezzanine cabinet";
    case "corner45":
      return `${cornerSide(module)} blind-corner wardrobe: one straight rectangular carcass along the ${cornerRun(module) === "back" ? "back" : "side"} wall reaching deep into the room corner; its corner part is hidden behind the neighbouring wardrobe on the other wall; in front, next to that neighbour, a narrow fixed filler strip in the door finish, then one straight overlay door hinged at the outer end; no diagonal or bevelled parts`;
    case "end":
      return "open finishing end module";
    case "shelving":
      return module.shelvingKind === "chest" ? "chest of drawers (low dresser), every column is a stack of pull-out drawers" : "open grid shelving unit";
  }
};

/** Декор Egger для ИИ-рендера: артикул, название и образец цвета. */
const decorEnglish = (decor: ReturnType<typeof resolveDecor>) => `Egger ${decor.code} "${decor.name}" (screen swatch ${decor.hex})`;

/** Задник в своём декоре: название Egger и образец цвета для ИИ-рендера. */
const backDecorEnglish = (module: WardrobeModule) => decorEnglish(resolveDecor(module.backDecor));

const shelvingDescription = (module: WardrobeModule, project: PlannerProject) => {
  const grid = getShelvingGridLayout(module);
  const cellLayouts = new Map(getShelvingCellLayouts(module, grid).map(layout => [layout.id, layout]));
  const columns = grid.columns;
  const rows = grid.rows;
  const total = columns * rows;
  const cells = module.shelvingCells ?? [];
  const diagonalSections = getShelvingDiagonalSections(module, grid);
  const diagonalCellCount = diagonalSections.reduce(
    (sum, section) => sum + section.columnSpan * section.rowSpan,
    0
  );
  const doors = cells.filter((cell) => cell.content === "door").length;
  const drawerCells = cells.filter((cell) => cell.content === "drawers").length;
  const open = Math.max(0, total - doors - drawerCells - diagonalCellCount);
  const angledMainDividers = grid.columnDividerCenters.flatMap((_, dividerIndex) =>
    grid.cellHeights.map((__, row) =>
      getShelvingMainDividerSegment(module, dividerIndex, row, grid)
    )
  ).filter((segment) => Boolean(segment && Math.abs(segment.rotationY ?? 0) >= 0.5));
  return [
    module.shelvingBase === "legs"
      ? `${grid.baseHeight} mm visible legs; the bottom panel and both outer side panels start above the legs, with no side panel extending down to the floor`
      : module.shelvingBase === "plinth"
        ? `${grid.baseHeight} mm recessed plinth; the outer side panels extend to the floor`
        : "floor-standing carcass with both outer side panels extending to the floor and no separate legs or plinth",
    `${columns} columns by ${rows} rows (${total} rectangular cells)`,
    `clear column widths from left: ${grid.cellWidths.map(Math.round).join(" / ")} mm`,
    // Ряды со своими ширинами (№ 19): стойки этого ряда стоят по-своему.
    ...grid.rowCustomColumns.flatMap((custom, row) => custom
      ? [`row ${row + 1} from floor has its own upright positions: clear cell widths from left ${grid.rowCellWidths[row]!.map(Math.round).join(" / ")} mm, so its uprights do not line up with the rows above and below`]
      : []),
    `clear row heights from bottom: ${grid.cellHeights.map(Math.round).join(" / ")} mm`,
    ...cells.flatMap(cell => getShelvingCellBoards(cell,
      cellLayouts.get(cell.id)?.width ?? gridCellWidth(grid, cell.row, cell.column),
      cellLayouts.get(cell.id)?.height ?? grid.cellHeights[cell.row] ?? 0, module.depth)
      .map(board => `cell column ${cell.column + 1}, row ${cell.row + 1} from floor: local ${board.glass ? "clear transparent glass shelf (not wood, the objects below it stay visible through it)" : board.kind} ${Math.round(board.width)} by ${Math.round(board.height)} mm, centre ${Math.round(board.x)} mm from cell left and ${Math.round(board.y)} mm from cell bottom${Math.abs(board.rotationY ?? 0) >= 0.5
        ? `, rotated ${Math.abs(board.rotationY ?? 0).toFixed(1)} degrees around its vertical axis with its front edge pointing ${(board.rotationY ?? 0) < 0 ? "left" : "right"}`
        : ""}`)),
    ...angledMainDividers.map(segment => {
      if (!segment) return "";
      const edges = shelvingDividerEdgeCenters(segment);
      return `main divider between columns ${segment.dividerIndex + 1} and ${segment.dividerIndex + 2}, row ${segment.row + 1} from floor: rotated ${Math.abs(segment.rotationY ?? 0).toFixed(1)} degrees around its vertical axis, rear centre ${Math.round(edges.rear + module.width / 2)} mm and front centre ${Math.round(edges.front + module.width / 2)} mm from shelving left edge`;
    }),
    ...getShelvingMergedCells(module, grid).map(section =>
      `merged cell at column ${section.column + 1}, row ${section.row + 1} from floor spans ${section.columnSpan} columns by ${section.rowSpan} rows as one opening, with no grid shelf or divider inside it`),
    ...shelvingLightingCells(module, grid).map(light => {
      const colour = `${LIGHT_TEMPERATURES[light.temperature].kelvin} K ${light.temperature === "warm" ? "warm white" : "cool white"}`;
      const depth = light.position === "back" ? "near the back panel, washing the back of the cell" : "near the front edge, lighting the objects on the shelf";
      const fixture = light.lighting === "horizontal"
        ? "a LED strip in an aluminium profile under the top shelf"
        : "LED strips in aluminium profiles along both inner side uprights";
      const each = light.compartments > 1 ? ` in each of its ${light.compartments} compartments` : "";
      return `cell column ${light.column + 1}, row ${light.row + 1} from floor is lit by ${fixture}${each}, ${depth}, ${colour}; light falls off naturally from the strip, only that cell glows`;
    }),
    ...shelvingModuleLights(module).map(light => light.kind === "plinth"
      ? `a ${LIGHT_TEMPERATURES[light.temperature].kelvin} K LED strip under the bottom casts a soft glow onto the floor in front of the shelving`
      : `a ${LIGHT_TEMPERATURES[light.temperature].kelvin} K LED contour on the back casts a soft halo onto the wall around the top and sides of the shelving, making it look floating`),
    `${open} open cells`,
    `${doors} cells with doors`,
    ...cells.filter((cell) => cell.content === "door" && cell.doorOpening).map((cell) => {
      const rect = shelvingCellFrontRect(module, cell.id, grid);
      const front = rect ? cellFrontLeaves(cell, rect.width, rect.height, shelvingCellHeadroomMm(project, module, cell.id, grid), shelvingExcludedLifts(module)) : null;
      return front?.opening === "double"
        ? `cell column ${cell.column + 1}, row ${cell.row + 1} from floor has a pair of hinged doors meeting in the middle with a thin seam`
        : front?.flap
          ? `cell column ${cell.column + 1}, row ${cell.row + 1} from floor has one ${flapMotionEnglish(front.flap)} instead of a side-hinged door`
          : "";
    }).filter(Boolean),
    `${drawerCells} cells with drawers`,
    // Комод (16.10): над ящиками колонки — откидной вверх фасад.
    ...cells.filter((cell) => cell.content === "drawers" && cell.chestTopFlap && module.shelvingKind === "chest").map((cell) =>
      `column ${cell.column + 1} has a ${cell.chestTopFlapGlass ? "framed glass " : ""}lift-up flap front about ${Math.round(cell.chestTopFlap!)} mm high at the top, opening upwards on a stay lift, with ${shelvingCellDrawerCountById(module, cell)} pull-out drawers below it`),
    doors + drawerCells > 0
      ? shelvingFacadeMount(module) === "inset"
        ? "door and drawer fronts are inset: set inside the openings flush with the front edges, the upright and shelf edges stay visible around every front with an even 2 mm gap"
        : "door and drawer fronts are overlay: they cover the front edges of the uprights and shelves, adjacent fronts meet with a thin 3 mm seam"
      : "",
    diagonalSections.length
      ? diagonalSections
          .map(
            (section, index) =>
              `diagonal diamond-lattice section ${index + 1}: starts at column ${section.column + 1}, row ${section.row + 1} from floor and replaces exactly ${section.columnSpan} columns by ${section.rowSpan} rows`
          )
          .join("; ")
      : "no diagonal lattice sections",
    module.shelvingBackPanel
      ? hasOwnBackDecor(module)
        ? `one full back panel in its own contrasting decor ${backDecorEnglish(module)}, different from the carcass`
        : "one full back panel in the carcass finish"
      : "no full back panel",
    angledMainDividers.length || cells.some(cell => cell.extraDivider && cell.extraDividerMode === "angled")
      ? "only explicitly listed divider segments are yaw-rotated in depth; all remaining dividers are straight"
      : "all dividers outside diagonal sections are straight"
  ].filter(Boolean).join(", ");
};

const moduleSpecification = (
  module: WardrobeModule,
  project: PlannerProject,
  index: number,
  interiorProps = true
) => {
  const boardMaterial = materialDescription[module.material ?? project.carcassMaterial];
  const enamel = shelvingCarcassEnamel(module);
  const finishHex =
    module.type === "shelving"
      ? enamel?.hex ?? normalizeShelvingColorHex(module.shelvingColorHex)
      : undefined;
  const finishCode = enamel ? enamel.code : normalizeShelvingColorCode(module.shelvingColorCode);
  const shelvingFinish = finishHex
    ? `${enamel ? "satin painted (enamel) MDF" : "solid-colour finish"} ${finishCode ?? finishHex}, exact screen-preview swatch ${finishHex}`
    : undefined;
  const carcass = shelvingFinish
    ? `${boardMaterial}, fully covered in ${shelvingFinish}`
    : boardMaterial;
  const dimensions = module.type === "corner45"
    ? `${cornerPlan(module).length} mm long along the wall, depth ${module.depth} mm, height ${module.height} mm; door ${cornerDoorWidthMm(module)} mm, filler ${cornerFillerMm(module)} mm in front of the blind part and a second filler ${cornerNeighbourFillerMm(module)} mm on the face of the neighbouring cabinet at the corner (the two filler strips meet at a right angle)`
    : `${module.width} mm wide x ${module.height} mm high x ${module.depth} mm deep`;
  const baseType = cabinetBaseType(module);
  const installation =
    module.type === "mezzanine" ||
    module.type === "shelving" ||
    module.type === "corner45"
      ? ""
      : ` INSTALLATION: ${
          baseType === "floor"
            ? "the carcass stands directly on the floor with no plinth or legs"
            : baseType === "legs"
              ? `four visible support legs, ${cabinetBaseHeightMm(module)} mm high`
              : `${cabinetBaseHeightMm(module)} mm high recessed plinth, set back ${cabinetBaseSetbackMm(module)} mm from the front`
        }; filler panels are exactly ${leftFillerMm(module)} mm left, ${rightFillerMm(module)} mm right and ${topFillerMm(module)} mm above.`;
  const cornerFilling = module.type === "corner45" ? ` INSIDE (shelves and rails only): ${module.items.map(item =>
    item.type === "rail" ? `rail over the full length of the carcass, continuing into the corner behind the neighbouring wardrobe, axis ${item.y} mm above floor`
      : `full-length rectangular shelf at ${item.y} mm above floor`).join("; ") || "empty"}.` : "";
  const ownBack = hasOwnBackDecor(module) && (module.type !== "shelving" || shelvingHasBackPanels(module))
    ? ` BACK PANEL: ${module.type === "shelving" ? "every back panel" : "the inner back panel"} is in its own decor ${backDecorEnglish(module)}, contrasting with the carcass; keep this contrast visible.`
    : "";
  // Полки и ящики в своём декоре (16.10).
  const drawerFront = drawerFrontFinish(module);
  const interiorDecor = `${hasOwnShelfDecor(module)
    ? ` SHELVES: every interior shelf is in its own decor ${decorEnglish(resolveDecor(module.shelfDecor))}, not the carcass decor.` : ""}${drawerFront.kind === "decor"
    ? ` DRAWERS: the front panel of every interior drawer is in decor ${decorEnglish(resolveDecor(drawerFront.code))}.`
    : drawerFront.kind === "facade" ? " DRAWERS: the front panel of every interior drawer matches the door finish." : ""}`;
  const cornerHinges = module.type === "corner45" && module.door === "hinged"
    ? ` HINGES: the door is hinged at the corner side, next to the filler strip (on the ${cornerHingeSide(module) < 0 ? "left" : "right"} as seen from the front); the handle is at the outer end. The neighbouring cabinet's door is also hinged at the corner, so both doors open towards each other.`
    : "";
  const base = `MODULE ${index + 1}: ${moduleTypeDescription(module)}, ${dimensions}, carcass ${carcass}.${installation}${cornerFilling}${cornerHinges}${ownBack}${interiorDecor}`;
  const sectionWidths = cabinetSectionWidths(module.width);
  const structure = cabinetDividerOffsets(module).length > 0
    ? ` STRUCTURE: one continuous carcass with ${sectionWidths.length - 1} shared full-depth internal upright${sectionWidths.length > 2 ? "s" : ""}; clear structural bays follow nominal widths ${sectionWidths.join(" / ")} mm. Do not draw separate adjacent cabinets or double the shared uprights. Every shelf, drawer, basket and rail sits inside one bay between uprights; nothing passes through an upright.`
    : "";

  if (module.type === "shelving") {
    // Предметы интерьера скрыты кнопкой «Предметы» — телевизора нет и на снимке.
    const tv = interiorProps ? shelvingTvPlacement(project, module) : null;
    const tvText = !tv ? ""
      : tv.mount === "stand"
        ? " DECOR ON TOP: a 55-inch flat TV on its own two feet stands centred on the top surface, with a slim soundbar in front of the screen. They are decor, not part of the furniture."
        : ` DECOR ON THE WALL: a 65-inch flat TV is wall-mounted centred above the unit, its lower edge ${tv.bottomMm} mm above the floor. It is decor, not part of the furniture.`;
    return `${base} GRID IS EXACT: ${shelvingDescription(module, project)}. Preserve every divider and every cell; do not add doors, objects or contents to open cells.${tvText}`;
  }

  if (module.type === "end") {
    return `${base} OPEN END MODULE: ${endModuleDescription(module)}. It has no door, handle or loose decor. Preserve the shaped open shelves and its exact left/right orientation.`;
  }

  if (module.type === "mezzanine" && hasUnifiedFacade(project, baseModuleFor(project, module))) {
    return `${base}${structure} NO SEPARATE DOORS: the tall hinged doors of the cabinet below continue over this mezzanine up to its top edge as single leaves; there is no horizontal seam, handle or hinge line between the cabinet and the mezzanine fronts.`;
  }

  if (module.door === "open") {
    const openDrawers = module.type === "mezzanine" ? null : cabinetDrawerLayout(module);
    const drawersText = openDrawers
      ? ` EXTERNAL DRAWERS: ${openDrawers.count} pull-out drawers with ${openDrawers.frontHeightMm} mm fronts at the bottom of each bay, below the open shelves.`
      : "";
    return `${base}${structure}${drawersText} NO DOORS: the interior is intentionally open and contains ${groupedItems(module)}. Preserve only those visible fittings; do not add clothes or decor.`;
  }

  const leaves = doorLeafCount(module);
  const halfOverlayLeaves = hingedDoorLeafLayout(module).filter(
    leaf => leaf.hingeApplication === "half-overlay"
  ).length;
  const hingeHardware = halfOverlayLeaves > 0
    ? ` HINGES: outside leaves use full-overlay concealed hinges; ${halfOverlayLeaves} leaf${halfOverlayLeaves === 1 ? "" : "s"} mounted to shared ${CARCASS_MM} mm uprights use cranked half-overlay adjacent-door hinges on opposite faces of the same upright. Keep a 2 mm reveal between the paired fronts; never draw a doubled upright.`
    : "";
  const flaps = mezzanineFlapLayout(module, moduleHeadroomMm(project, module));
  const doorMotion = module.door === "sliding" ? "sliding"
    : flaps ? flapMotionEnglish(flaps[0]!.front)
      : "hinged";
  const plinthHeight =
    module.type === "mezzanine" ? 0 : cabinetBaseHeightMm(module);
  // Наружные ящики внизу: двери начинаются над ними.
  const outerDrawers = module.type === "mezzanine" ? null : cabinetDrawerLayout(module);
  const doorBottom = outerDrawers ? outerDrawers.doorBottomMm : plinthHeight + 2;
  const drawerFronts = outerDrawers
    ? ` EXTERNAL DRAWERS: at the bottom of ${outerDrawers.bays.length > 1 ? `each of the ${outerDrawers.bays.length} bays` : "the cabinet"} there are ${outerDrawers.count} pull-out drawers with their own fronts ${cabinetDrawerFinishDiffers(module) ? `in plain opaque chipboard in the facade decor, NOT ${module.facade === "mirror" ? "mirror" : "glass"} like the doors above` : "in the same finish"}, each front ${outerDrawers.frontHeightMm} mm high, with a ${CABINET_DRAWER_SEAM_MM} mm open finger-pull gap above every front (the fronts have NO handles: they are pulled by the top edge); the lowest front starts ${outerDrawers.frontsBottomMm} mm above the floor. The hinged doors start ${CABINET_DRAWER_SEAM_MM} mm above the top drawer front.`
    : "";
  const doorVerticalPosition =
    module.type === "mezzanine"
      ? `the lower door edge is ${doorBottom} mm above this module's own bottom edge`
      : baseType === "floor"
        ? `the cabinet stands directly on the room floor with no plinth or legs, and the lower door edge is exactly ${doorBottom} mm above the room floor`
        : baseType === "legs"
          ? `the cabinet stands on ${plinthHeight} mm support legs, and the lower door edge is exactly ${doorBottom} mm above the room floor`
          : `the cabinet stands on the room floor, has a ${plinthHeight} mm recessed plinth, and the lower door edge is exactly ${doorBottom} mm above the room floor`;
  const facade = facadeDescription[module.facade];
  const requestedPattern =
    module.facadePattern ?? (module.facade === "gloss" ? "art009" : "plain");
  const pattern = facadeSupportsMilling(module.facade)
    ? requestedPattern
    : "plain";
  const facadeMaterial = materialDescription[
    module.facadeMaterial ?? module.material ?? project.carcassMaterial
  ];
  const facadeColor = facadePaintLabel(module);
  const facadeColorHex = facadePaintHex(module);
  const facadeFinish =
    module.facade === "wood"
      ? `wood/laminate decor ${facadeMaterial}`
      : module.facade === "satin" || module.facade === "gloss"
        ? `painted MDF/enamel color specified as ${facadeColor}, exact screen-preview swatch ${facadeColorHex}; the carcass decor does NOT tint or recolor the door faces`
        : module.facade === "glass"
          ? "colorless transparent clear glass; do not tint it with the carcass decor"
          : "neutral silver mirror; do not tint it with the carcass decor";
  const leafWidth = module.type === "corner45"
    ? cornerDoorWidthMm(module)
    : module.width / Math.max(1, leaves);
  // Единый фасад: створки корпуса идут до верха антресоли.
  const unifiedExtra = hasUnifiedFacade(project, module) ? mezzanineFor(project, module.id)?.height ?? 0 : 0;
  const doorHeight = Math.max(120, module.height + unifiedExtra - (outerDrawers ? outerDrawers.doorBottomMm - 2 : plinthHeight));
  const handle =
    module.handle === "push" || module.handle === "glassGrip"
      ? handleDescription[module.handle]
      : `${handleDescription[module.handle]}, ${handleLengthMm(
          module,
          leafWidth,
          doorHeight
        )} mm long, ${handleOrientation(module)}, centre ${handleCenterYmm(
          module,
          leafWidth,
          doorHeight
        )} mm above the door bottom and ${handleEdgeOffsetMm(
          module,
          leafWidth,
          doorHeight
        )} mm from the opening edge`;
  // Створки со своим фасадом (16.10) — по номерам, слева направо.
  const ownLeaves = module.door === "hinged"
    ? hingedDoorLeafLayout(module).filter((leaf) => doorFacadeOverride(module, leaf.index)).map((leaf) => {
        const own = leafFinish(module, leaf.index);
        const name = module.type === "corner45" ? "the corner wardrobe door" : `door leaf ${leaf.index + 1} counted from the left`;
        const finish = own.facade === "wood"
          ? `wood/laminate decor ${materialDescription[own.facadeMaterial ?? module.material ?? project.carcassMaterial]}`
          : own.facade === "satin" || own.facade === "gloss"
            ? `painted MDF/enamel ${facadePaintLabel(own)}, swatch ${facadePaintHex(own)}`
            : own.facade === "glass" ? "clear glass in an aluminium frame" : "silver mirror";
        return `${name} is different: ${facadeDescription[own.facade]}, ${finish}`;
      })
    : [];
  const leafOverrides = ownLeaves.length ? ` INDIVIDUAL LEAVES: ${ownLeaves.join("; ")}; every other leaf follows the front described above.` : "";
  const visibility =
    module.facade === "glass"
      ? `The glass may reveal exactly these fittings: ${groupedItems(module)}.`
      : module.facade === "mirror"
        ? "The mirror shows only a plausible reflection of the existing room; it is not transparent and the cabinet interior is invisible."
        : "The closed facade is completely opaque. The cabinet interior, clothes, rails, shelves and drawers must NOT be visible through it.";

  return `${base}${structure} EXACT FRONT: ${leaves} ${doorMotion} door leaf${leaves === 1 ? "" : "s"}; ${facade}; ${patternDescription[pattern]}; finish ${facadeFinish}; ${handle}.${leafOverrides}${hingeHardware}${drawerFronts} DOOR VERTICAL POSITION: ${doorVerticalPosition}; ${unifiedExtra ? `the leaves are single tall fronts ${doorHeight - 8} mm high that continue over the mezzanine above: the upper edge is 6 mm below the mezzanine top, with no seam at the cabinet top` : "the upper edge is 6 mm below the module top"}. ${flaps ? `Each front is one horizontal panel per bay: ${flapMotionEnglish(flaps[0]!.front)}. ` : ""}Every open hinged leaf remains perfectly upright and rotates only around its vertical hinge axis. Its lower edge keeps the same elevation as when closed and must never touch, rest on, cut into or visually merge with the floor. Preserve this exact front color from the source image; lighting may change highlights and shadows but must not recolor the facade. ${visibility}`;
};

export const createProjectRenderSpecification = (project: PlannerProject, options: { interiorProps?: boolean } = {}) =>
  [
    `The source contains exactly ${project.modules.length} furniture modules.`,
    "The following module descriptions are authoritative. Do not reinterpret routed or recessed solid panels as glazing.",
    ...project.modules.map((module, index) =>
      moduleSpecification(module, project, index, options.interiorProps ?? true)
    )
  ].join("\n");
